import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Eye, 
  Settings, 
  TrendingUp,
  Clock,
  Target,
  FileText,
  Search
} from 'lucide-react';

interface QualityRule {
  id: string;
  name: string;
  description: string;
  type: 'accuracy' | 'completeness' | 'consistency' | 'timeliness';
  threshold: number;
  status: 'active' | 'inactive';
  violations: number;
  lastCheck: string;
}

interface ValidationResult {
  id: string;
  billId: string;
  supplierName: string;
  ruleId: string;
  ruleName: string;
  status: 'passed' | 'failed' | 'warning';
  confidence: number;
  timestamp: string;
  details: string;
}

export function QualityControlValidation() {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  const qualityRules: QualityRule[] = [
    {
      id: '1',
      name: 'Minimum Confidence Threshold',
      description: 'All processed bills must have minimum 85% confidence score',
      type: 'accuracy',
      threshold: 85,
      status: 'active',
      violations: 12,
      lastCheck: '2 minutes ago'
    },
    {
      id: '2',
      name: 'Required Field Validation',
      description: 'Bill amount, date, and supplier must be present',
      type: 'completeness',
      threshold: 100,
      status: 'active',
      violations: 3,
      lastCheck: '5 minutes ago'
    },
    {
      id: '3',
      name: 'Amount Range Validation',
      description: 'Bill amounts must be within reasonable ranges for supplier type',
      type: 'consistency',
      threshold: 95,
      status: 'active',
      violations: 7,
      lastCheck: '1 minute ago'
    },
    {
      id: '4',
      name: 'Processing Time Limit',
      description: 'Bills must be processed within 30 seconds',
      type: 'timeliness',
      threshold: 30,
      status: 'inactive',
      violations: 0,
      lastCheck: '1 hour ago'
    }
  ];

  const validationResults: ValidationResult[] = [
    {
      id: '1',
      billId: 'BILL-001',
      supplierName: 'Energy Corp',
      ruleId: '1',
      ruleName: 'Minimum Confidence Threshold',
      status: 'failed',
      confidence: 82,
      timestamp: '2 minutes ago',
      details: 'Confidence score below threshold (82% < 85%)'
    },
    {
      id: '2',
      billId: 'BILL-002',
      supplierName: 'Power Solutions',
      ruleId: '2',
      ruleName: 'Required Field Validation',
      status: 'passed',
      confidence: 98,
      timestamp: '3 minutes ago',
      details: 'All required fields present'
    },
    {
      id: '3',
      billId: 'BILL-003',
      supplierName: 'Utility Services',
      ruleId: '3',
      ruleName: 'Amount Range Validation',
      status: 'warning',
      confidence: 91,
      timestamp: '5 minutes ago',
      details: 'Amount higher than typical range for supplier'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed': return <CheckCircle className="h-4 w-4" />;
      case 'failed': return <XCircle className="h-4 w-4" />;
      case 'warning': return <AlertTriangle className="h-4 w-4" />;
      default: return <Shield className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'accuracy': return <Target className="h-4 w-4" />;
      case 'completeness': return <FileText className="h-4 w-4" />;
      case 'consistency': return <Shield className="h-4 w-4" />;
      case 'timeliness': return <Clock className="h-4 w-4" />;
      default: return <Settings className="h-4 w-4" />;
    }
  };

  const overallScore = 92;
  const totalChecks = 1250;
  const passedChecks = 1125;
  const failedChecks = 85;
  const warningChecks = 40;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Quality Control & Validation</h1>
          <p className="text-muted-foreground">Monitor and maintain data quality standards</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">Run Full Audit</Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button>Add Quality Rule</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Quality Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="rule-name">Rule Name</Label>
                  <Input id="rule-name" placeholder="Enter rule name" />
                </div>
                <div>
                  <Label htmlFor="rule-type">Type</Label>
                  <select className="w-full p-2 border rounded" id="rule-type">
                    <option value="accuracy">Accuracy</option>
                    <option value="completeness">Completeness</option>
                    <option value="consistency">Consistency</option>
                    <option value="timeliness">Timeliness</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="threshold">Threshold</Label>
                  <Input id="threshold" type="number" placeholder="Enter threshold value" />
                </div>
                <Button className="w-full">Create Rule</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Quality Score Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallScore}%</div>
            <Progress value={overallScore} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-600">+2.1%</span> from last week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Passed Checks</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{passedChecks.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {((passedChecks / (totalChecks || 1)) * 100).toFixed(1)}% of total checks
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed Checks</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{failedChecks}</div>
            <p className="text-xs text-muted-foreground">
              {((failedChecks / (totalChecks || 1)) * 100).toFixed(1)}% of total checks
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Warnings</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{warningChecks}</div>
            <p className="text-xs text-muted-foreground">
              {((warningChecks / (totalChecks || 1)) * 100).toFixed(1)}% of total checks
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="rules">Quality Rules</TabsTrigger>
          <TabsTrigger value="results">Validation Results</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Quality Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      <span className="text-sm">Accuracy</span>
                    </div>
                    <span className="text-sm font-medium">94.2%</span>
                  </div>
                  <Progress value={94.2} />
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">Completeness</span>
                    </div>
                    <span className="text-sm font-medium">97.8%</span>
                  </div>
                  <Progress value={97.8} />
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      <span className="text-sm">Consistency</span>
                    </div>
                    <span className="text-sm font-medium">89.5%</span>
                  </div>
                  <Progress value={89.5} />
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">Timeliness</span>
                    </div>
                    <span className="text-sm font-medium">92.1%</span>
                  </div>
                  <Progress value={92.1} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Issues</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {validationResults.slice(0, 5).map((result) => (
                    <div key={result.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className={getStatusColor(result.status).split(' ')[0]}>
                        {getStatusIcon(result.status)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{result.billId}</div>
                        <div className="text-xs text-muted-foreground">{result.details}</div>
                      </div>
                      <div className="text-xs text-muted-foreground">{result.timestamp}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="rules" className="space-y-6">
          <div className="grid gap-4">
            {qualityRules.map((rule) => (
              <Card key={rule.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getTypeIcon(rule.type)}
                      <div>
                        <CardTitle className="text-lg">{rule.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{rule.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={rule.status === 'active' ? 'default' : 'secondary'}>
                        {rule.status}
                      </Badge>
                      <Button variant="outline" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="font-medium">Type</div>
                      <div className="text-muted-foreground capitalize">{rule.type}</div>
                    </div>
                    <div>
                      <div className="font-medium">Threshold</div>
                      <div className="text-muted-foreground">{rule.threshold}%</div>
                    </div>
                    <div>
                      <div className="font-medium">Violations</div>
                      <div className="text-muted-foreground">{rule.violations}</div>
                    </div>
                    <div>
                      <div className="font-medium">Last Check</div>
                      <div className="text-muted-foreground">{rule.lastCheck}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="results" className="space-y-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search validation results..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-4">
            {validationResults.map((result) => (
              <Card key={result.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={getStatusColor(result.status)}>
                        {getStatusIcon(result.status)}
                      </div>
                      <div>
                        <div className="font-medium">{result.billId} - {result.supplierName}</div>
                        <div className="text-sm text-muted-foreground">{result.ruleName}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-medium">Confidence: {result.confidence}%</div>
                        <div className="text-xs text-muted-foreground">{result.timestamp}</div>
                      </div>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {result.details}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="text-lg">Daily Quality Report</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Comprehensive daily quality metrics and trends
                </p>
                <Button className="w-full">Generate Report</Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="text-lg">Supplier Quality Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Quality performance by supplier comparison
                </p>
                <Button className="w-full">Generate Report</Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="text-lg">Rule Effectiveness Report</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Analysis of quality rule performance and impact
                </p>
                <Button className="w-full">Generate Report</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}